/**
 * Незбережені зміни подання в адресі (FLT-07, FLT-08; views.md, розд. 6): `/l/<подання>?d=<diff>`,
 * де `diff` — компактний JSON лише відмінностей у base64url, без стиснення. Розбір синхронний, до
 * першого запиту: адресу читає `loader` маршруту і екран (app/routes/platform.ts). Подання вже в
 * шляху, тож окремого `view=` немає. Перехід з навігації (чипи, меню) іде без `d` — чисте подання.
 *
 * Що несе `d` (E4a-W3): сортування з шапки (`s`, «поле:напрям»). Стовпці, ширини й закріплення —
 * особистий шар подання на сервері (view-prefs.ts, views.md, розд. 5): подання «зміненим» не
 * роблять і в адресу не йдуть.
 *
 * Фільтр (E4a-W4): `f` — дерево мови умов, розібране `parseFilterTree` профілю `filter`
 * (`@clientline/contracts/filter-tree`), або `null` — людина прибрала умову подання. Зіпсоване
 * дерево — зіпсоване посилання. Пошуковий текст і умови на поля `personal` / `sensitive` в адресу
 * не пишемо (SEC-110): таку чернетку екран тримає в пам'яті вкладки (`addressable`), а стану на
 * сервері (`?state=`) ще немає.
 *
 * Модуль — у першому екрані (його читає `loader`): лише кодек, без UI.
 */
import type { FilterNode, MetaEntity, ViewConfig, ViewSort } from '@clientline/contracts';
import { parseFilterTree } from '@clientline/contracts/filter-tree';
import { SORT_VALUE } from './list-context.ts';

/** Що людина змінила поверх збереженого подання. Порожньо — подання як є. */
export interface ViewDraft {
  sort?: ViewSort | undefined;
  /**
   * Умова фільтра поверх подання (E4a-W4): замінює `config.filter`; `null` — без умови взагалі
   * (людина прибрала умову подання). Немає — умова подання.
   */
  filter?: FilterNode | null | undefined;
}

/** Адреса списку: лише сирий `d`, перевірений на алфавіт і довжину (розбір — `decodeDraft`). */
export interface ListSearch {
  d?: string | undefined;
}

/**
 * Найдовший `d`: довший стан views.md, розд. 6, зберігає на сервері (~1 500 символів адреси). Довше
 * — посилання не наше або зіпсоване: відкривається чисте подання.
 */
export const DRAFT_MAX_LENGTH = 1500;
const BASE64URL = /^[A-Za-z0-9_-]+$/;

/** `validateSearch` маршруту `/l/$view`: лише `d`, інші параметри адреси відкидаються. */
export function listSearch(search: Record<string, unknown>): ListSearch {
  const d = search.d;
  return typeof d === 'string' && d.length > 0 && d.length <= DRAFT_MAX_LENGTH ? { d } : {};
}

function toBase64Url(text: string): string {
  let binary = '';
  for (const byte of new TextEncoder().encode(text)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(value: string): string {
  const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
}

/** Чернетка → `d`; порожня — `undefined` (адреса без `d`). */
export function encodeDraft(draft: ViewDraft): string | undefined {
  const body: Record<string, unknown> = {};
  if (draft.sort) body.s = `${draft.sort.field}:${draft.sort.dir}`;
  if (draft.filter !== undefined) body.f = draft.filter;
  return Object.keys(body).length > 0 ? toBase64Url(JSON.stringify(body)) : undefined;
}

/**
 * Результат розбору `d`: `invalid` — посилання зіпсоване чи з іншої версії (екран відкриває чисте
 * подання й каже про це). Невідомі ключі мовчки відкидаються: це сумісність наперед.
 */
export type DecodedDraft = { ok: true; draft: ViewDraft } | { ok: false };

export function decodeDraft(raw: string | undefined): DecodedDraft {
  if (raw === undefined) return { ok: true, draft: {} };
  if (raw.length > DRAFT_MAX_LENGTH || !BASE64URL.test(raw)) return { ok: false };
  let body: unknown;
  try {
    body = JSON.parse(fromBase64Url(raw));
  } catch {
    return { ok: false };
  }
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return { ok: false };
  const { s, f } = body as { s?: unknown; f?: unknown };
  const draft: ViewDraft = {};
  if (s !== undefined) {
    if (typeof s !== 'string' || !SORT_VALUE.test(s)) return { ok: false };
    const [field, dir] = s.split(':') as [string, 'asc' | 'desc'];
    draft.sort = { field, dir };
  }
  if (f === null) draft.filter = null;
  else if (f !== undefined) {
    const parsed = parseFilterTree(f);
    if (!parsed.ok) return { ok: false };
    draft.filter = parsed.tree;
  }
  return { ok: true, draft };
}

/** Чи є незбережені зміни: позначка «• змінено» і кнопки «Зберегти» / «Скинути» (FLT-07). */
export const isDirty = (draft: ViewDraft): boolean =>
  draft.sort !== undefined || draft.filter !== undefined;

/**
 * Чи можна чернетку писати в адресу (SEC-110): умови лише на поля, чий клас з `/meta` відомий і не
 * `personal` / `sensitive` (поля немає, класу немає чи клас невідомий — не можна, fail closed), і
 * код не довший за `DRAFT_MAX_LENGTH`.
 */
export function addressable(draft: ViewDraft, entity: MetaEntity): boolean {
  if (draft.filter) {
    const parsed = parseFilterTree(draft.filter);
    if (!parsed.ok) return false;
    for (const condition of parsed.conditions) {
      const cls = entity.fields.find((f) => f.key === condition.field)?.classification;
      if (cls !== 'public' && cls !== 'internal') return false;
    }
  }
  return (encodeDraft(draft)?.length ?? 0) <= DRAFT_MAX_LENGTH;
}

/**
 * Конфігурація подання з чернеткою поверх: «Зберегти» (FLT-07) і «Зберегти як нове» (FLT-02).
 * Сортування з шапки — один рівень (DAL у MVP сортує за одним полем), фільтр замінює умову подання.
 */
export function applyDraft(config: ViewConfig, draft: ViewDraft): ViewConfig {
  const { filter: _old, ...rest } = config;
  const filter = draft.filter === undefined ? config.filter : (draft.filter ?? undefined);
  return {
    ...rest,
    ...(draft.sort ? { sort: [draft.sort] } : {}),
    ...(filter ? { filter } : {}),
  };
}
